import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { AdminCatalogItemForm } from "../../types";
import {
  selectAdminCatalogItemForm,
  selectIsAddItemModalOpen,
} from "../../redux/selector";
import { adminCatalogActions } from "../../redux/slice";
import { useStyles } from "../AddCatalogModal/styles";

type FieldConfig = {
  key: keyof AdminCatalogItemForm;
  label: string;
  type: "text" | "textarea";
};

const ROWS: FieldConfig[][] = [
  [{ key: "itemCode", label: "ITEM_CODE", type: "text" }],
  [{ key: "itemName", label: "ITEM_NAME", type: "text" }],
  [
    { key: "profileHeight", label: "PROFILE_HEIGHT", type: "text" },
    { key: "weightPerMeter", label: "WEIGHT_PER_METER", type: "text" },
  ],
  [{ key: "technicalNote", label: "TECHNICAL_NOTE", type: "textarea" }],
];

const REQUIRED_FIELDS: Array<keyof AdminCatalogItemForm> = [
  "itemCode",
  "itemName",
  "profileHeight",
  "weightPerMeter",
  "technicalNote",
];

const P = "CATALOGS.ITEMS.MODAL";

const AddCatalogItemModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddItemModalOpen);
  const form = useSelector(selectAdminCatalogItemForm);

  const onClose = () => {
    dispatch(adminCatalogActions.closeAddItemModal());
  };

  const setField = (name: keyof AdminCatalogItemForm, value: string) =>
    dispatch(adminCatalogActions.setItemFormField({ name, value }));

  const placeholder = (label: string) => t(`${P}.PLACEHOLDERS.ENTER_${label}`);

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const renderField = ({ key, label, type }: FieldConfig) => (
    <Box key={key} className={styles.field}>
      <Typography className={styles.label}>{t(`${P}.FIELDS.${label}`)}</Typography>
      <TextField
        className={type === "textarea" ? styles.descriptionInput : styles.input}
        fullWidth
        multiline={type === "textarea"}
        variant="outlined"
        placeholder={placeholder(label)}
        value={form[key]}
        InputProps={{ notched: false }}
        onChange={(event) => setField(key, event.target.value)}
      />
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {ROWS.map((row, index) =>
            row.length > 1 ? (
              <Box key={index} className={styles.grid}>
                {row.map(renderField)}
              </Box>
            ) : (
              renderField(row[0])
            )
          )}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid}
            onClick={() => dispatch(adminCatalogActions.addCatalogItem())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddCatalogItemModal;
